import BlockContent from '@sanity/block-content-to-react'
import React, { FC, useMemo } from 'react'

import { MenuEntryContentFragment, SanityMenuEntryStaticContent } from '../../../../graphql/gatsby'
import useSanityImage from '../../../../lib/sanity/hooks/useSanityImage'
import { imageUrlFor } from '../../../../utils/image-url'
import { isSanityImageWithAssetId } from '../../../../utils/sanity/image'

export interface MenuEntryStaticContentProps {
  staticCardContent: MenuEntryContentFragment['staticContent']
}

export const MenuEntryStaticContent: FC<MenuEntryStaticContentProps> = ({ staticCardContent }) => {
  const hasValidImage =
    staticCardContent?.image && isSanityImageWithAssetId(staticCardContent.image)

  // Use high DPI image URL for primary display
  const imageUrl = useSanityImage(
    { source: hasValidImage && staticCardContent?.image ? staticCardContent.image : undefined },
    {
      width: 243,
      height: 243,
      dpr: 2,
      fit: 'max',
    },
  )

  // Generate srcset for responsive images
  const srcSet = useMemo(() => {
    if (!hasValidImage || !staticCardContent?.image) return undefined

    const imageBuilder = imageUrlFor(staticCardContent.image).auto('format').fit('max')
    return [
      `${imageBuilder.width(165).height(165).url()} 165w`,
      `${imageBuilder.width(243).height(243).url()} 243w`,
      `${imageBuilder.width(330).height(330).dpr(2).url()} 330w`,
      `${imageBuilder.width(486).height(486).dpr(2).url()} 486w`,
    ].join(', ')
  }, [hasValidImage, staticCardContent?.image])

  // Need to default markDefs to avoid build error (see issue at https://github.com/sanity-io/sanity/issues/7797)
  const blocks = staticCardContent?._rawDescription.map(
    (block: SanityMenuEntryStaticContent['_rawDescription']) => ({
      ...block,
      markDefs: block.markDefs || [],
    }),
  )

  return (
    <div className="flex items-start flex-col md:flex-row gap-3.5 lg:gap-5 max-w-[30rem] lg:max-w-4xl">
      {imageUrl && (
        <img
          src={imageUrl}
          srcSet={srcSet}
          sizes="(max-width: 1023px) 165px, 243px"
          alt=""
          loading="eager"
          className="mx-auto w-[165px] lg:w-[243px] flex-none"
        />
      )}
      <div className="flex flex-col gap-3.5 lg:gap-4 py-2 lg:py-4 text-center md:text-left cursor-text">
        <h3 className="text-2xl lg:text-[2rem] leading-[1.2] font-medium">
          {staticCardContent?.title}
        </h3>
        <div className="text-xs lg:text-lg leading-[1.5] font-normal">
          <BlockContent blocks={blocks} />
        </div>
      </div>
    </div>
  )
}
